import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Box,
  CircularProgress,
} from '@mui/material';
import { useLogger } from '../hooks/useLogger';
import { IEto, IUpdateEto } from '../models/iEto';

interface IEditEtoDialogProps {
  open: boolean;
  eto: IEto | null;
  onClose: () => void;
  onSave: (etoId: number, data: IUpdateEto) => Promise<boolean>;
}

export const EditEtoDialog = ({ open, onClose, onSave, eto }: IEditEtoDialogProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const logger = useLogger('EditEtoDialog');
  const [productDescription, setProductDescription] = useState('');
  const [catalog, setCatalog] = useState('');

  useEffect(() => {
    if (eto) {
      setProductDescription(eto.productDescription);
      setCatalog(eto.catalog);
    } else {
      // Reset form when dialog is closed or eto is null
      setProductDescription('');
      setCatalog('');
    }
  }, [eto]);

  const handleSave = async () => {
    if (!eto) return;

    const updateData: IUpdateEto = {
      productDescription,
      catalog,
    };

    setIsSaving(true);
    try {
      const success = await onSave(eto.etoId, updateData);
      if (success) {
        onClose();
      }
    } catch (error) {
      logger.UI.error('Update operation failed', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit ETO Record</DialogTitle>
      <DialogContent>
        {eto && (
          <Box component="form" sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Sales Org"
                  value={eto.salesOrgName}
                  variant="filled"
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Product Y Number"
                  value={eto.productYNumber}
                  variant="filled"
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Product Description"
                  value={productDescription}
                  onChange={(e) => setProductDescription(e.target.value)}
                  disabled={isSaving}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Catalog"
                  value={catalog}
                  onChange={(e) => setCatalog(e.target.value)}
                  disabled={isSaving}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={isSaving}>
          {isSaving ? <CircularProgress size={24} /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
